import React from "react";
import { Button } from "@/components/ui/button";
import Section from "@/components/ui/section";

import { cn, tinaFieldSafe } from "@/utils";
import { useGlobalData } from "./TinaContext";
import { useIsEditing } from "./ui/page";
import { PencilIcon } from "lucide-react";

type ButtonCallToAction = {
  label: string;
  link: string;
  variant?: "default" | "outline";
};

type GlobalCtaType =
  | "cta"
  | "resourceCta"
  | "advisoryHeaderCta"
  | "advisoryCta"
  | "solutionsCta"
  | "teamCta";

export type CallToActionBackgroundVariant =
  | "pixels"
  | "pixels-subtle"
  | "dots"
  | "none";

interface CallToActionProps {
  title?: string;
  description?: string | any;
  showDescription?: boolean;
  buttons?: ButtonCallToAction[];
  variant?: "center" | "left";
  wrapperClassName?: string;
  backgroundVariant?: CallToActionBackgroundVariant;
  primaryFirst?: boolean;
  _content_source?: any;
  globalCtaType?: GlobalCtaType;
}

export default function CallToAction({
  title: titleProp,
  description: descriptionProp,
  showDescription = true,
  buttons: buttonsProp,
  variant = "left",
  backgroundVariant = "pixels-subtle",
  wrapperClassName,
  _content_source,
  globalCtaType = "cta",
  primaryFirst = false,
}: CallToActionProps) {
  const globalData = useGlobalData();
  const { isEditing, isOpen } = useIsEditing();

  // Get the correct global CTA based on the type
  const globalCta = globalData?.[globalCtaType];

  const title = titleProp || globalCta?.title;
  const description =
    descriptionProp === undefined ||
      descriptionProp === null ||
      descriptionProp === ""
      ? globalCta?.description
      : descriptionProp;
  const buttons = buttonsProp || globalCta?.buttons;

  return (
    <Section
      wrapperClassName={cn("relative overflow-hidden", wrapperClassName)}
      backgroundVariant="pixels-subtle-reverse"
    // backgroundVariant={backgroundVariant}
    >
      <div
        className={cn("relative z-10", {
          "text-center flex flex-col items-center gap-6": variant === "center",
          "grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-4 md:gap-8": variant === "left",
        })}
      >
        <h2
          className={cn("h3 text-balance", {
            "row-span-2": variant === "left",
            "max-w-[840px] mx-auto ": variant === "center",
          })}
          data-tina-field={
            titleProp
              ? tinaFieldSafe({ _content_source }, "title")
              : tinaFieldSafe(globalData, `${globalCtaType}.title`)
          }
        >
          {title}
        </h2>
        {description && showDescription && (
          <p
            className={cn("p max-w-[840px] text-left", {
              "text-center": variant === "center",
            })}
            data-tina-field={
              descriptionProp
                ? tinaFieldSafe({ _content_source }, "description")
                : tinaFieldSafe(globalData, `${globalCtaType}.description`)
            }
          >
            {description}
          </p>
        )}
        <div className="flex items-center gap-4 max-md:mt-4">
          {buttons?.map((button: ButtonCallToAction, buttonIndex: number) => (
            <Button
              key={buttonIndex}
              variant={
                (primaryFirst ? buttonIndex === 0 : buttonIndex !== 0)
                  ? "default"
                  : "outline"
              }
              href={button?.link}
              data-tina-field={
                buttonsProp
                  ? tinaFieldSafe({ _content_source }, `buttons.${buttonIndex}`)
                  : tinaFieldSafe(
                    globalData,
                    `${globalCtaType}.buttons.${buttonIndex}`
                  )
              }
            >
              {button?.label}
              {title ? (
                <span className="sr-only"> — {title}</span>
              ) : null}
            </Button>
          ))}
        </div>
      </div>
      {isEditing && isOpen && (
        <div
          className="absolute -top-12 right-8 flex items-center gap-2 text-sm px-2 py-1"
          data-tina-field={tinaFieldSafe(globalData, `${globalCtaType}.title`)}
        >
          <PencilIcon className="w-4 h-4 text-blue-500" />
          Edit Global
        </div>
      )}
    </Section>
  );
}
